import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  ArrowRight,
  Building2,
  Siren,
  Server,
  Users,
  ScrollText,
  LayoutDashboard,
  Sun,
  Moon,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useUI } from '@/lib/store';
import { useTenants } from '@/api/tenants';

type CommandKind = 'nav' | 'tenant' | 'action';

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  kind: CommandKind;
  group: string;
  keywords?: string;
  run: (nav: ReturnType<typeof useNavigate>) => void;
  shortcut?: string;
}

export function CommandPalette() {
  const open = useUI(s => s.paletteOpen);
  const setOpen = useUI(s => s.setPaletteOpen);
  const togglePalette = useUI(s => s.togglePalette);
  const toggleActivity = useUI(s => s.toggleActivity);
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Global ⌘K + / shortcut
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        togglePalette();
      }
      if (e.key === '/' && !open && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen, togglePalette]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const { data: apiTenants } = useTenants();

  const items = useMemo<CommandItem[]>(() => {
    const navItems: CommandItem[] = [
      { id: 'nav-overview', label: 'Overview', icon: LayoutDashboard, kind: 'nav', group: 'Go to', run: n => n('/'), shortcut: 'G O' },
      { id: 'nav-tenants', label: 'Customers', icon: Building2, kind: 'nav', group: 'Go to', run: n => n('/tenants'), shortcut: 'G C' },
      { id: 'nav-incidents', label: 'Incidents', icon: Siren, kind: 'nav', group: 'Go to', run: n => n('/incidents'), shortcut: 'G I' },
      { id: 'nav-logs', label: 'Logs', icon: ScrollText, kind: 'nav', group: 'Go to', run: n => n('/logs'), shortcut: 'G L' },
      { id: 'act-logs-errors', label: 'Logs: errors only', icon: ScrollText, kind: 'action', group: 'Actions', keywords: 'error log filter', run: n => n('/logs?levels=error') },
      { id: 'nav-fleet', label: 'Infrastructure', icon: Server, kind: 'nav', group: 'Go to', run: n => n('/fleet'), shortcut: 'G F' },
      { id: 'nav-staff', label: 'Staff & access', icon: Users, kind: 'nav', group: 'Go to', run: n => n('/staff') },
      { id: 'nav-audit', label: 'Audit log', icon: ScrollText, kind: 'nav', group: 'Go to', run: n => n('/audit'), shortcut: 'G A' },
    ];

    const tenantItems: CommandItem[] = (apiTenants ?? []).map(t => ({
      id: `tenant-${t.id}`,
      label: t.name,
      hint: t.owner_email,
      icon: Building2,
      kind: 'tenant' as const,
      group: 'Customers',
      keywords: `${t.slug} ${t.owner_email} ${t.name}`,
      run: n => n(`/tenants/${t.slug}`),
    }));

    const actionItems: CommandItem[] = [
      { id: 'act-incident', label: 'Declare incident', icon: Siren, kind: 'action', group: 'Actions', run: n => n('/incidents'), shortcut: 'I' },
      { id: 'act-activity', label: 'Toggle activity panel', icon: ScrollText, kind: 'action', group: 'Actions', run: () => toggleActivity(), shortcut: '⇧ A' },
      { id: 'act-theme-light', label: 'Theme: Light', icon: Sun, kind: 'action', group: 'Preferences', keywords: 'theme light', run: () => setTheme('light') },
      { id: 'act-theme-dark', label: 'Theme: Dark', icon: Moon, kind: 'action', group: 'Preferences', keywords: 'theme dark', run: () => setTheme('dark') },
    ];

    return [...navItems, ...tenantItems, ...actionItems];
  }, [toggleActivity, apiTenants]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const ql = q.toLowerCase();
    return items.filter(i =>
      (i.label + ' ' + (i.keywords ?? '') + ' ' + (i.hint ?? '')).toLowerCase().includes(ql),
    );
  }, [items, q]);

  // Reset active on filter change
  useEffect(() => {
    setActive(0);
  }, [q]);

  // Group filtered items
  const grouped = useMemo(() => {
    const groups: { name: string; items: CommandItem[] }[] = [];
    for (const item of filtered) {
      let g = groups.find(x => x.name === item.group);
      if (!g) {
        g = { name: item.group, items: [] };
        groups.push(g);
      }
      g.items.push(item);
    }
    return groups;
  }, [filtered]);

  const flatItems = filtered;

  // Keyboard nav inside palette
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(a => Math.min(flatItems.length - 1, a + 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(a => Math.max(0, a - 1));
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = flatItems[active];
        if (item) {
          item.run(nav);
          setOpen(false);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, active, flatItems, nav, setOpen]);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-active="true"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  let runningIdx = -1;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center px-4 pt-[12vh] animate-fade-in"
      onClick={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        onClick={e => e.stopPropagation()}
        className="relative w-full max-w-xl bg-bg-elevated border border-border rounded-xl shadow-pop overflow-hidden"
      >
        <div className="flex items-center gap-2 px-4 h-12 border-b border-border">
          <Search size={16} className="text-fg-subtle shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Type a command, search tenants, jump to a page…"
            className="flex-1 bg-transparent border-0 outline-none text-fg text-md placeholder:text-fg-subtle"
          />
          <kbd className="text-2xs text-fg-subtle font-mono bg-surface-2 border border-border rounded px-1.5 h-5 inline-flex items-center">
            esc
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-2">
          {grouped.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-fg-muted">
              No matches for <span className="font-mono text-fg">{q}</span>
            </div>
          ) : (
            grouped.map(group => (
              <div key={group.name} className="mb-1.5">
                <div className="px-3 py-1 text-2xs uppercase tracking-wider font-semibold text-fg-subtle">
                  {group.name}
                </div>
                <ul>
                  {group.items.map(item => {
                    runningIdx += 1;
                    const isActive = runningIdx === active;
                    const Icon = item.icon;
                    return (
                      <li key={item.id}>
                        <button
                          data-active={isActive}
                          onMouseEnter={() => setActive(runningIdx)}
                          onClick={() => {
                            item.run(nav);
                            setOpen(false);
                          }}
                          className={cn(
                            'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors',
                            isActive ? 'bg-surface-2 text-fg' : 'text-fg-muted hover:text-fg',
                          )}
                        >
                          <Icon size={15} className="shrink-0 text-fg-muted" />
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-fg truncate">{item.label}</span>
                            {item.hint && (
                              <span className="block text-2xs text-fg-subtle truncate">{item.hint}</span>
                            )}
                          </span>
                          {item.shortcut && (
                            <kbd className="text-2xs text-fg-subtle font-mono bg-surface-2 border border-border rounded px-1.5 h-5 inline-flex items-center">
                              {item.shortcut}
                            </kbd>
                          )}
                          {isActive && <ArrowRight size={12} className="text-fg-subtle ml-1" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-border bg-surface-2/40 px-3 py-2 text-2xs text-fg-subtle flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <kbd className="font-mono bg-bg border border-border rounded px-1 h-3.5 inline-flex items-center">↑↓</kbd> navigate
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="font-mono bg-bg border border-border rounded px-1 h-3.5 inline-flex items-center">↵</kbd> select
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="font-mono bg-bg border border-border rounded px-1 h-3.5 inline-flex items-center">esc</kbd> close
            </span>
          </div>
          <span>NousViz Cloud Admin · {flatItems.length} results</span>
        </div>
      </div>
    </div>
  );
}

function isTypingTarget(t: EventTarget | null) {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

function setTheme(t: 'light' | 'dark') {
  document.documentElement.setAttribute('data-theme', t);
  try {
    localStorage.setItem('nv:theme', t);
  } catch (_) {}
}
